<template>
  <div class="bg-background min-h-screen">
    <div class="container mx-auto px-4 py-12 max-w-4xl">

      <!-- Header -->
      <div class="mb-10">
        <h1 class="text-3xl font-semibold tracking-tight">My Subscription</h1>
        <p class="text-sm text-muted-foreground mt-1.5">Your current plan and billing details.</p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center py-24">
        <div class="h-9 w-9 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="rounded-xl border border-destructive/20 bg-destructive/5 p-5 text-sm text-destructive text-center">
        {{ error }}
      </div>

      <div v-else class="flex flex-col gap-6">

        <!-- ── LEFT COLUMN: Plan card ── -->

        <!-- Free plan card -->
        <div
          v-if="planType === 'free'"
          class="rounded-2xl overflow-hidden border border-green-500/30 shadow-md"
        >
          <!-- Gradient header -->
          <div class="bg-gradient-to-br from-green-600 to-green-500 px-6 py-8 flex flex-col gap-4">
            <div class="flex items-start justify-between">
              <div class="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
                <i class="fas fa-check-circle text-white text-2xl"></i>
              </div>
              <span class="inline-flex items-center gap-1.5 rounded-full border border-white/40 bg-white/15 px-3 py-1 text-xs font-semibold text-white">
                <span class="h-1.5 w-1.5 rounded-full bg-white"></span>
                Active
              </span>
            </div>
            <div>
              <p class="text-2xl font-bold text-white leading-tight">Free Plan</p>
              <p class="text-sm text-white/70 mt-1">No expiry · Free plan limits</p>
            </div>
          </div>

          <!-- Upgrade CTA body -->
          <div class="bg-card px-6 py-6 flex flex-col gap-4">
            <p class="text-sm text-muted-foreground leading-relaxed">
              Upgrade to Basic or Premium for more AI cover letters, more filters, instant alerts and Auto Apply.
            </p>
            <router-link
              :to="{ name: 'SubscriptionPlans' }"
              class="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors w-fit"
            >
              <i class="fas fa-arrow-up text-xs"></i> Upgrade Plan
            </router-link>
          </div>
        </div>

        <!-- Paid plan card -->
        <div
          v-else-if="subscription"
          class="rounded-2xl overflow-hidden shadow-md"
          :class="isExpired ? 'border border-red-500/30' : 'border border-border/60'"
        >
          <!-- Gradient header — color per plan type -->
          <div
            class="px-6 py-8 flex flex-col gap-4"
            :class="[
              isExpired
                ? 'bg-gradient-to-br from-red-600 to-red-500'
                : planType === 'premium'
                  ? 'bg-gradient-to-br from-violet-600 to-violet-500'
                  : planType === 'test'
                    ? 'bg-gradient-to-br from-amber-500 to-amber-400'
                    : 'bg-gradient-to-br from-blue-600 to-blue-500'
            ]"
          >
            <div class="flex items-start justify-between">
              <!-- Plan icon -->
              <div class="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
                <i :class="[planIconName, 'text-white text-2xl']"></i>
              </div>
              <!-- Status pill -->
              <span
                class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold"
                :class="isExpired
                  ? 'border-red-200/60 bg-red-100/20 text-white'
                  : 'border-white/40 bg-white/15 text-white'"
              >
                <span :class="['h-1.5 w-1.5 rounded-full', isExpired ? 'bg-red-200' : 'bg-white']"></span>
                {{ capitalize(subscription.status) }}
              </span>
            </div>
            <!-- Plan name + duration -->
            <div>
              <p class="text-2xl font-bold text-white leading-tight">{{ subscription.plan.name }}</p>
              <p class="text-sm text-white/70 mt-1">{{ planDurationLabel }}</p>
            </div>
            <!-- Expired banner -->
            <div v-if="isExpired" class="flex items-center gap-2 rounded-xl bg-white/15 border border-white/20 px-4 py-3 text-white">
              <i class="fas fa-exclamation-circle text-base shrink-0"></i>
              <span class="text-sm font-medium">This plan has expired</span>
            </div>
          </div>

          <!-- Detail rows body -->
          <div class="bg-card px-6 py-5 flex flex-col gap-0">
            <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Billing details</p>
            <dl class="divide-y divide-border/60 text-sm">
              <div class="flex justify-between py-3">
                <dt class="text-muted-foreground">Price paid</dt>
                <dd class="font-semibold">{{ isTrial ? 'Free trial' : formatPeso(subscription.plan.price) }}</dd>
              </div>
              <div class="flex justify-between py-3">
                <dt class="text-muted-foreground">Start date</dt>
                <dd class="font-medium">{{ formatDate(subscription.start_date) }}</dd>
              </div>
              <div v-if="subscription.end_date" class="flex justify-between py-3">
                <dt class="text-muted-foreground">{{ isExpired ? 'Expired on' : (isTrial ? 'Trial ends on' : 'Ends on') }}</dt>
                <dd :class="['font-medium', isExpired ? 'text-red-600 dark:text-red-400' : '']">{{ formatDate(subscription.end_date) }}</dd>
              </div>
            </dl>

            <!-- Low days warning -->
            <div
              v-if="!isExpired && subscription.days_remaining != null && subscription.days_remaining <= 7"
              class="mt-4 flex items-start gap-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3.5 py-3 text-sm text-amber-700 dark:text-amber-300"
            >
              <i class="fas fa-exclamation-triangle mt-0.5 shrink-0"></i>
              <span>Your plan expires soon. Resubscribe to keep access.</span>
            </div>

            <!-- CTA -->
            <div class="pt-5">
              <router-link
                :to="{ name: 'SubscriptionPlans' }"
                :class="[
                  'inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-colors',
                  isExpired
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border border-border bg-background hover:bg-muted text-foreground'
                ]"
              >
                <i :class="isExpired ? 'fas fa-redo' : 'fas fa-arrow-up'" class="text-xs"></i>
                {{ isExpired ? 'Resubscribe' : 'Upgrade / Change Plan' }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- No subscription data fallback -->
        <div v-else class="rounded-2xl border border-border/80 bg-muted/30 p-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-3">
          <i class="fas fa-inbox text-3xl opacity-30"></i>
          <p>No subscription found.</p>
          <router-link
            :to="{ name: 'SubscriptionPlans' }"
            class="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            View Plans
          </router-link>
        </div>

        <!-- ── RIGHT COLUMN: Plan Usage card ── -->
        <div
          class="rounded-2xl border shadow-sm overflow-hidden"
          :class="[
            planType === 'premium' ? 'border-violet-500/30 bg-violet-500/[0.06]' :
            planType === 'test'    ? 'border-amber-500/30 bg-amber-500/[0.06]' :
            planType === 'free'    ? 'border-green-500/30 bg-green-500/[0.05]' :
                                     'border-primary/20 bg-primary/[0.03]'
          ]"
        >
          <!-- Card header -->
          <div class="px-6 pt-6 pb-4 border-b"
            :class="[
              planType === 'premium' ? 'border-violet-500/20' :
              planType === 'test'    ? 'border-amber-500/20' :
              planType === 'free'    ? 'border-green-500/20' :
                                       'border-primary/15'
            ]"
          >
            <div class="flex items-center justify-between">
              <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Plan Usage</h2>
              <span v-if="usageLoading" class="h-3.5 w-20 animate-pulse rounded bg-muted"></span>
            </div>
          </div>

          <div class="px-6 py-6 flex flex-col gap-6">

            <!-- Usage: AI Cover Letters -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <span class="flex items-center gap-2 text-sm font-medium">
                  <i class="fas fa-magic w-4 text-center text-base" :class="planAccentText"></i>
                  AI Cover Letters
                </span>
                <span v-if="usageLoading" class="h-3.5 w-16 animate-pulse rounded bg-muted"></span>
                <span v-else-if="aiQuota && (aiQuota.unlimited || !aiQuota.applies)" class="text-xs font-semibold text-green-600 dark:text-green-400">
                  Unlimited
                </span>
                <span v-else-if="aiQuota" class="text-xs text-muted-foreground tabular-nums">
                  {{ aiQuota.used }} / {{ aiQuota.limit }}
                  <span class="ml-1 text-muted-foreground/60">{{ aiQuota.period === 'lifetime' ? 'lifetime' : 'this month' }}</span>
                </span>
                <span v-else class="text-xs text-muted-foreground">—</span>
              </div>
              <template v-if="aiQuota && aiQuota.applies">
                <div class="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    :class="['h-full rounded-full transition-all duration-500', planAccentBar]"
                    :style="{ width: aiCoverLetterBarPct + '%' }"
                  ></div>
                </div>
                <p v-if="aiQuota.remaining != null" class="mt-1.5 text-xs text-muted-foreground">
                  {{ aiQuota.remaining }} remaining
                </p>
              </template>
              <div v-else-if="!usageLoading && (!aiQuota || !aiQuota.applies)" class="h-2 w-full rounded-full bg-muted/40"></div>
            </div>

            <!-- Usage: Job Filter Presets -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <span class="flex items-center gap-2 text-sm font-medium">
                  <i class="fas fa-filter w-4 text-center text-base" :class="planAccentText"></i>
                  Job Filter Presets
                </span>
                <span v-if="usageLoading" class="h-3.5 w-16 animate-pulse rounded bg-muted"></span>
                <span v-else-if="jobFilterLimit >= 99999999" class="text-xs font-semibold text-green-600 dark:text-green-400">
                  Unlimited
                </span>
                <span v-else-if="jobFilterLimit != null" class="text-xs text-muted-foreground tabular-nums">
                  {{ jobFilterCount }} / {{ jobFilterLimit }} saved
                </span>
                <span v-else class="text-xs text-muted-foreground">—</span>
              </div>
              <template v-if="jobFilterLimit && jobFilterLimit > 0 && jobFilterLimit < 99999999">
                <div class="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    :class="['h-full rounded-full transition-all duration-500', planAccentBar]"
                    :style="{ width: jobFilterBarPct + '%' }"
                  ></div>
                </div>
                <p class="mt-1.5 text-xs text-muted-foreground">
                  {{ Math.max(0, jobFilterLimit - jobFilterCount) }} slot{{ Math.max(0, jobFilterLimit - jobFilterCount) !== 1 ? 's' : '' }} remaining
                </p>
              </template>
              <div v-else-if="!usageLoading && (jobFilterLimit == null || jobFilterLimit === 0)" class="h-2 w-full rounded-full bg-muted/40"></div>
            </div>

            <!-- Divider -->
            <div class="border-t border-border/60 -mx-1"></div>

            <!-- Feature flag section header -->
            <div>
              <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Features</p>

              <div class="flex flex-col gap-2">

                <!-- Auto-apply -->
                <div class="flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
                  <span class="flex items-center gap-3 text-sm font-medium text-foreground">
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-lg" :class="planType === 'premium' ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300' : 'bg-primary/10 text-primary'">
                      <i class="fas fa-robot text-xs"></i>
                    </span>
                    Auto-apply
                  </span>
                  <span v-if="plan">
                    <span :class="limitBadgeClass(autoApplyCell(plan))">{{ autoApplyCell(plan) }}</span>
                  </span>
                </div>

                <!-- AI Follow-ups / month — disabled, commented out
                <div v-if="FOLLOW_UPS_ENABLED && plan && plan.has_application_followup" class="flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
                  <span class="flex items-center gap-3 text-sm font-medium text-foreground">
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-lg" :class="planType === 'premium' ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300' : 'bg-primary/10 text-primary'">
                      <i class="fas fa-reply text-xs"></i>
                    </span>
                    AI Follow-ups / month
                  </span>
                  <span v-if="!plan.max_ai_followups_per_month || plan.max_ai_followups_per_month === 0"
                        class="inline-flex items-center rounded-full bg-green-500/15 text-green-700 dark:text-green-300 px-2.5 py-0.5 text-xs font-bold">
                    Unlimited
                  </span>
                  <span v-else class="inline-flex items-center rounded-full bg-foreground/10 text-foreground px-2.5 py-0.5 text-xs font-bold">
                    {{ plan.max_ai_followups_per_month }} / mo
                  </span>
                </div>
                -->

                <!-- Telegram alerts -->
                <div class="flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
                  <span class="flex items-center gap-3 text-sm font-medium text-foreground">
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-lg" :class="planType === 'premium' ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300' : 'bg-primary/10 text-primary'">
                      <i class="fab fa-telegram text-xs"></i>
                    </span>
                    Telegram &amp; Discord alerts
                  </span>
                  <span v-if="plan && plan.has_telegram_alerts"
                        class="inline-flex items-center gap-1 rounded-full bg-green-500/15 text-green-700 dark:text-green-300 px-2.5 py-0.5 text-xs font-bold">
                    <i class="fas fa-check text-[9px]"></i> Included
                  </span>
                  <span v-else class="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium">
                    <i class="fas fa-times text-[9px]"></i> Not included
                  </span>
                </div>

                <!-- Email notifications -->
                <div class="flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
                  <span class="flex items-center gap-3 text-sm font-medium text-foreground">
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-lg" :class="planType === 'premium' ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300' : 'bg-primary/10 text-primary'">
                      <i class="fas fa-envelope text-xs"></i>
                    </span>
                    Email notifications
                  </span>
                  <span v-if="plan && plan.has_email_notifications"
                        class="inline-flex items-center gap-1 rounded-full bg-green-500/15 text-green-700 dark:text-green-300 px-2.5 py-0.5 text-xs font-bold">
                    <i class="fas fa-check text-[9px]"></i> Included
                  </span>
                  <span v-else class="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium">
                    <i class="fas fa-times text-[9px]"></i> Not included
                  </span>
                </div>

                <!-- Priority support — premium only -->
                <div v-if="plan && plan.has_priority_support" class="flex items-center justify-between rounded-lg bg-violet-500/10 border border-violet-500/20 px-4 py-3">
                  <span class="flex items-center gap-3 text-sm font-medium text-foreground">
                    <span class="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-500/15 text-violet-700 dark:text-violet-300">
                      <i class="fas fa-headset text-xs"></i>
                    </span>
                    Priority support
                  </span>
                  <span class="inline-flex items-center gap-1 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 px-2.5 py-0.5 text-xs font-bold">
                    <i class="fas fa-check text-[9px]"></i> Included
                  </span>
                </div>

              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import apiClient from "@/services/api";
import authStore from "@/store/auth";
import { autoApplyCell } from "@/composables/useSubscriptionPlans";
import { FOLLOW_UPS_ENABLED } from "@/config/features";

const planMeta = {
  basic:   { iconName: "fas fa-star",  iconClasses: "bg-primary/10 ring-primary/5 text-primary",       borderBg: "border-primary/20 bg-primary/5",       textColor: "text-primary",     accentBar: "bg-primary",    accentText: "text-primary" },
  premium: { iconName: "fas fa-crown", iconClasses: "bg-violet-500/15 ring-violet-500/10 text-violet-700 dark:text-violet-300", borderBg: "border-violet-500/30 bg-violet-500/10", textColor: "text-violet-700 dark:text-violet-300", accentBar: "bg-violet-500", accentText: "text-violet-600 dark:text-violet-400" },
  test:    { iconName: "fas fa-flask", iconClasses: "bg-amber-500/15 ring-amber-500/10 text-amber-700 dark:text-amber-300",   borderBg: "border-amber-500/30 bg-amber-500/10",   textColor: "text-amber-700 dark:text-amber-300",  accentBar: "bg-amber-400",  accentText: "text-amber-600 dark:text-amber-400" },
  free:    { iconName: "fas fa-check-circle", iconClasses: "bg-green-500/15 ring-green-500/10 text-green-700 dark:text-green-300", borderBg: "border-green-500/30 bg-green-500/10", textColor: "text-green-700 dark:text-green-300", accentBar: "bg-green-500", accentText: "text-green-600 dark:text-green-400" },
};

export default {
  name: "SubscriptionPage",
  setup() {
    const loading = ref(true);
    const error = ref(null);
    const subscription = ref(null);

    // Usage data — loaded non-blocking after main subscription fetch
    const usageLoading = ref(false);
    const aiQuota = ref(null);
    const jobFilterCount = ref(0);

    const isLoggedIn = computed(() => authStore.state.isLoggedIn);
    const planType = computed(() => subscription.value?.plan?.plan_type ?? "free");
    const isExpired = computed(() => subscription.value?.status === "expired");
    const isTrial = computed(() => subscription.value?.status === "trial");

    // Limits that apply right now: the Free plan once a paid plan or trial has
    // lapsed, even before the expiry job moves the subscription onto Free.
    const plan = computed(() => subscription.value?.effective_plan ?? subscription.value?.plan ?? null);

    // Plan limit for job filter presets (0 = unlimited in some setups; null = not loaded)
    const jobFilterLimit = computed(() => plan.value?.max_job_filter_presets ?? null);

    const meta = computed(() => planMeta[planType.value] ?? planMeta.basic);
    const planIconName = computed(() => meta.value.iconName);
    const planIconClasses = computed(() => meta.value.iconClasses);
    const planBorderBg = computed(() => meta.value.borderBg);
    const planTextColor = computed(() => meta.value.textColor);
    const planAccentBar = computed(() => meta.value.accentBar);
    const planAccentText = computed(() => meta.value.accentText);

    // Progress bar percentages — clamped 0–100
    const aiCoverLetterBarPct = computed(() => {
      if (!aiQuota.value || !aiQuota.value.applies || !aiQuota.value.limit) return 0;
      return Math.min(100, Math.round((aiQuota.value.used / aiQuota.value.limit) * 100));
    });

    const jobFilterBarPct = computed(() => {
      const limit = jobFilterLimit.value;
      if (!limit || limit === 0) return 0;
      return Math.min(100, Math.round((jobFilterCount.value / limit) * 100));
    });

    const planDurationLabel = computed(() => {
      if (isTrial.value) return "14-day free trial";
      const days = subscription.value?.plan?.duration_days;
      if (!days) return "";
      if (days <= 31) return "Monthly plan";
      if (days <= 92) return "3-month plan";
      return "1-year plan";
    });

    // Pill style for a formatted limit: green = unlimited, muted = not included.
    const limitBadgeClass = (text) => {
      const base = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold";
      if (text === "Unlimited") return `${base} bg-green-500/15 text-green-700 dark:text-green-300`;
      if (text === "Not included") return `${base} bg-muted text-muted-foreground font-medium`;
      return `${base} bg-foreground/10 text-foreground`;
    };

    const formatPeso = (price) => {
      if (price == null || price === "") return "-";
      const n = Number(price);
      if (Number.isNaN(n)) return "-";
      return `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (iso) => {
      if (!iso) return "";
      return new Date(iso).toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" });
    };

    const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

    const fetchSubscription = async () => {
      try {
        loading.value = true;
        error.value = null;
        const res = await apiClient.get("/auth/subscription/current/");
        subscription.value = res.data;
      } catch (err) {
        if (err.response?.status !== 404) {
          error.value = "Failed to load subscription details. Please try again.";
        }
      } finally {
        loading.value = false;
      }
    };

    // Non-blocking: fetch AI quota and filter preset count in parallel
    const fetchUsageData = async () => {
      usageLoading.value = true;
      try {
        const [dashRes, filtersRes] = await Promise.allSettled([
          apiClient.get("/dashboard/summary/"),
          apiClient.get("/job-filter-presets/"),
        ]);

        if (dashRes.status === "fulfilled") {
          aiQuota.value = dashRes.value.data?.sidebar?.ai_quota ?? null;
        }

        if (filtersRes.status === "fulfilled") {
          const data = filtersRes.value.data;
          // Handle both plain array and paginated { results: [...] } shapes
          if (Array.isArray(data)) {
            jobFilterCount.value = data.length;
          } else if (data && Array.isArray(data.results)) {
            jobFilterCount.value = data.results.length;
          } else if (data && typeof data.count === "number") {
            jobFilterCount.value = data.count;
          }
        }
      } finally {
        usageLoading.value = false;
      }
    };

    onMounted(async () => {
      await authStore.checkAuthStatus();
      // Main subscription fetch is blocking (controls the loading spinner)
      await fetchSubscription();
      // Usage data loads in the background — card renders with skeleton placeholders
      fetchUsageData();
    });

    return {
      FOLLOW_UPS_ENABLED,
      autoApplyCell,
      limitBadgeClass,
      loading,
      error,
      subscription,
      isLoggedIn,
      planType,
      isExpired,
      isTrial,
      plan,
      jobFilterLimit,
      planIconName,
      planIconClasses,
      planBorderBg,
      planTextColor,
      planAccentBar,
      planAccentText,
      planDurationLabel,
      usageLoading,
      aiQuota,
      jobFilterCount,
      aiCoverLetterBarPct,
      jobFilterBarPct,
      formatPeso,
      formatDate,
      capitalize,
    };
  },
};
</script>
